/**
 * BETA — supplier e-invoices.
 */
import { httpError } from "../router.js";
import { paginate, matchSearch, newId, nowIso, todayYmd, dateOffsetYmd } from "./_utils.js";

const RECEIVED_STATUTS = new Set(["RECUE", "STOCK_VALIDE", "PAYEE"]);

export function register(route) {
  route("GET", "/efactures", (ctx) => listEfactures(ctx));
  route("GET", "/efactures/recu", (ctx) => listEfactures(ctx, RECEIVED_STATUTS));
  route("GET", "/efactures/mes-commandes", (ctx) => listEfactures(ctx));

  route("GET", "/efactures/:id", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.id === ctx.params.id);
    if (!e) httpError(404, "E-facture introuvable", "NOT_FOUND");
    return e;
  });

  route("POST", "/efactures", (ctx) => {
    const body = ctx.body || {};
    const frn = ctx.store.data.fournisseurs.find((f) => f.id === body.fournisseur_id) || ctx.store.data.fournisseurs[0];
    const id = newId("efact-new");
    const created = {
      id,
      numero: `EF-2026-${id.slice(-5).toUpperCase()}`,
      fournisseur_id: frn?.id,
      fournisseur: frn ? { id: frn.id, nom: frn.nom, telephone: frn.telephone, email: frn.email } : null,
      type: body.type || "FACTURE",
      qr_code: `QR-EF-${id}`,
      code_import: `IMPORT-${id.toUpperCase()}`,
      date_emission: body.date_emission || todayYmd(),
      date_reception: null,
      date_echeance: body.date_echeance || dateOffsetYmd(30),
      statut: "BROUILLON",
      statut_paiement: "NON_PAYE",
      montant_ht: 0,
      montant_tva: 0,
      montant_ttc: 0,
      montant_paye: 0,
      reste_a_payer: 0,
      notes: body.notes || "",
      bons_livraison: [],
      details: body.details || [],
      created_at: nowIso(),
      updated_at: nowIso(),
    };
    ctx.store.data.efactures.unshift(created);
    ctx.commit();
    return created;
  });

  route("PATCH", "/efactures/:id/draft", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.id === ctx.params.id);
    if (!e) httpError(404, "E-facture introuvable", "NOT_FOUND");
    if (e.statut !== "BROUILLON") httpError(400, "Seuls les brouillons sont modifiables", "NOT_DRAFT");
    Object.assign(e, ctx.body || {});
    e.updated_at = nowIso();
    ctx.commit();
    return e;
  });

  route("DELETE", "/efactures/:id/draft", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.id === ctx.params.id);
    if (!e) httpError(404, "E-facture introuvable", "NOT_FOUND");
    if (e.statut !== "BROUILLON") httpError(400, "Seuls les brouillons sont supprimables", "NOT_DRAFT");
    ctx.store.data.efactures = ctx.store.data.efactures.filter((x) => x.id !== e.id);
    ctx.commit();
    return { success: true };
  });

  route("POST", "/efactures/:id/send", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.id === ctx.params.id);
    if (!e) httpError(404, "E-facture introuvable", "NOT_FOUND");
    e.statut = "RECUE";
    e.date_reception = todayYmd();
    e.updated_at = nowIso();
    ctx.commit();
    return e;
  });

  // Trésorerie TWP-36 (W20, TD-39 (a)) — « Marquer payée » is retired server-side (410): the imported
  // facture d'achat is settled by the treasury. The mock answers the same, nothing is written.
  route("POST", "/efactures/:id/mark-paid", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.id === ctx.params.id);
    if (!e) httpError(404, "E-facture introuvable", "NOT_FOUND");
    httpError(
      410,
      `La facture ${e.numero} se règle depuis la trésorerie : ouvrez sa facture d'achat et choisissez « Régler ».`,
      "EFACTURE_PAIEMENT_PAR_TRESORERIE",
    );
  });

  route("POST", "/efactures/:id/bl", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.id === ctx.params.id);
    if (!e) httpError(404, "E-facture introuvable", "NOT_FOUND");
    const bl = {
      id: newId("bl"),
      numero: ctx.body?.numero || `BL-${e.numero}-${(e.bons_livraison.length || 0) + 1}`,
      date: ctx.body?.date || todayYmd(),
      montant_ht: ctx.body?.montant_ht || 0,
      montant_ttc: ctx.body?.montant_ttc || 0,
      statut: "RECU",
      accepted_by_pharmacy: false,
    };
    e.bons_livraison.push(bl);
    e.updated_at = nowIso();
    ctx.commit();
    return e;
  });

  route("PATCH", "/efactures/:id/bl/:bl_id", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.id === ctx.params.id);
    if (!e) httpError(404, "E-facture introuvable", "NOT_FOUND");
    const bl = e.bons_livraison.find((b) => b.id === ctx.params.bl_id);
    if (!bl) httpError(404, "BL introuvable", "NOT_FOUND_BL");
    Object.assign(bl, ctx.body || {});
    ctx.commit();
    return e;
  });

  route("GET", "/efactures/qr/:qr_code", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.qr_code === ctx.params.qr_code);
    if (!e) httpError(404, "QR introuvable", "NOT_FOUND");
    return e;
  });

  route("GET", "/efactures/import/:code", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.code_import === ctx.params.code);
    if (!e) httpError(404, "Code import introuvable", "NOT_FOUND");
    return e;
  });

  route("PATCH", "/efactures/:id/statut", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.id === ctx.params.id);
    if (!e) httpError(404, "E-facture introuvable", "NOT_FOUND");
    if (ctx.body?.statut) e.statut = ctx.body.statut;
    if (ctx.body?.notes) e.notes = ctx.body.notes;
    e.updated_at = nowIso();
    ctx.commit();
    return e;
  });

  route("POST", "/efactures/:id/demande-modification", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.id === ctx.params.id);
    if (!e) httpError(404, "E-facture introuvable", "NOT_FOUND");
    e.notes = (e.notes || "") + "\n[Demande modif] " + (ctx.body?.notes || "");
    ctx.commit();
    return { success: true, efacture: e };
  });

  route("POST", "/efactures/:id/repondre-modification", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.id === ctx.params.id);
    if (!e) httpError(404, "E-facture introuvable", "NOT_FOUND");
    e.notes = (e.notes || "") + "\n[Réponse] " + (ctx.body?.response || "");
    ctx.commit();
    return { success: true, efacture: e };
  });

  route("POST", "/efactures/:id/validate-stock", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.id === ctx.params.id);
    if (!e) httpError(404, "E-facture introuvable", "NOT_FOUND");
    for (const d of e.details || []) {
      const totalQty = (d.quantite || 0) + (d.quantite_offerte || 0);
      ctx.store.data.stockLots.push({
        id: newId("lot"),
        produit_id: d.produit_id,
        numero_lot: d.numero_lot || `LOT-${e.numero}-${d.id}`,
        quantite_initiale: totalQty,
        quantite_actuelle: totalQty,
        date_peremption: d.date_peremption,
        date_fabrication: todayYmd(),
        prix_achat_ht: d.prix_achat_ht,
        prix_achat_ttc: d.prix_achat_ttc,
        achat_id: null,
        statut: "ACTIF",
        location: "A1",
      });
      const p = ctx.store.data.produits.find((x) => x.id === d.produit_id);
      if (p) p.stock_actuel = (p.stock_actuel || 0) + totalQty;
    }
    e.statut = "STOCK_VALIDE";
    e.updated_at = nowIso();
    ctx.commit();
    return e;
  });

  route("POST", "/efactures/:id/br-pharmacy", (ctx) => {
    const e = ctx.store.data.efactures.find((x) => x.id === ctx.params.id);
    if (!e) httpError(404, "E-facture introuvable", "NOT_FOUND");
    e.statut = "STOCK_VALIDE";
    e.updated_at = nowIso();
    ctx.commit();
    return e;
  });

  route("POST", "/efactures/pre-commandes/new", (ctx) => {
    const body = ctx.body || {};
    const frn = ctx.store.data.fournisseurs.find((f) => f.id === body.fournisseur_id) || ctx.store.data.fournisseurs[0];
    const id = newId("pc-new");
    const created = {
      id,
      numero: `PC-2026-${id.slice(-5).toUpperCase()}`,
      fournisseur_id: frn?.id,
      fournisseur: frn ? { id: frn.id, nom: frn.nom } : null,
      type: "PRECOMMANDE",
      statut: "PRECOMMANDE_ENVOYEE",
      statut_paiement: "NON_PAYE",
      date_emission: todayYmd(),
      details: body.details || [],
      bons_livraison: [],
      montant_ht: 0,
      montant_tva: 0,
      montant_ttc: 0,
      created_at: nowIso(),
      updated_at: nowIso(),
    };
    ctx.store.data.efactures.unshift(created);
    ctx.commit();
    return created;
  });
}

function listEfactures(ctx, statutFilter = null) {
  const { params, store } = ctx;
  let rows = [...store.data.efactures];
  if (statutFilter) rows = rows.filter((e) => statutFilter.has(e.statut));
  if (params.statut) rows = rows.filter((e) => e.statut === params.statut);
  if (params.fournisseur_id) rows = rows.filter((e) => e.fournisseur_id === params.fournisseur_id);
  if (params.type) rows = rows.filter((e) => e.type === params.type);
  if (params.date_debut) rows = rows.filter((e) => e.date_emission >= params.date_debut);
  if (params.date_fin) rows = rows.filter((e) => e.date_emission <= params.date_fin);
  if (params.search) {
    rows = rows.filter((e) =>
      matchSearch(e, params.search, ["numero", "qr_code", "code_import"]) ||
      matchSearch(e.fournisseur || {}, params.search, ["nom"]),
    );
  }
  rows.sort((a, b) => (a.date_emission > b.date_emission ? -1 : 1));
  return paginate(rows, params);
}
